import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Route, Routes, Navigate, useLocation } from "react-router-dom";
import {
  BackArrow,
  TopProgress,
  Tabs,
} from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { projectService } from "../../../../api";
import { extractErrorMessage } from "../../../../utils/error-extractor";
import { usePageTitle } from "../../../../context/navigationContext";
import OverviewTab from "./overview";
import {
  Container,
  HeadWrapper,
  PageTitle,
  NavWrapper,
} from "./styles";

const ProjectDetails: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { viewAlert } = useUi();
  const { id } = useParams<{ id: string }>();

  const [project, setProject] = useState<any>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  usePageTitle(project?.name ? `${t("PROJECT")}: ${project.name}` : t("PROJECT"));

  const getActiveTabFromPath = () => {
    const path = location.pathname.split('/').pop();
    return path || 'overview';
  };

  const [activeTab, setActiveTab] = useState<string>(getActiveTabFromPath());

  useEffect(() => {
    const currentTab = getActiveTabFromPath();
    setActiveTab(currentTab);
  }, [location.pathname]);

  const handleTabChange = (tabSlug: string) => {
    setActiveTab(tabSlug);
    navigate(`/openstack/projects/${id}/${tabSlug}`);
  };

  const loadProject = () => {
    if (!id) {
      navigate("/openstack/projects");
      return;
    }

    setPageLoading(true);
    projectService
      .getProjectDetail(id)
      .then((res) => {
        setProject(res.data);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.detail ||
          extractErrorMessage(error?.response?.data) ||
          t("FAILED_TO_LOAD_PROJECT");
        viewAlert({
          severity: "error",
          message,
        });
        navigate("/openstack/projects");
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  useEffect(() => {
    loadProject();
  }, [id]);

  const tabs = [
    {
      title: t("OVERVIEW"),
      slug: "overview",
      id: "1",
    },
  ];

  if (pageLoading || !project) {
    return <TopProgress loading={pageLoading} />;
  }

  return (
    <Container>
      <TopProgress loading={pageLoading} />
      <BackArrow fallbackPath="/openstack/projects" />
      <HeadWrapper>
        <PageTitle>
          {t("PROJECT")}: {project.name}
        </PageTitle>
      </HeadWrapper>
      <NavWrapper>
        <div>
          <Tabs
            tabs={tabs}
            size="display"
            location={activeTab}
            onChange={handleTabChange}
          />
        </div>
      </NavWrapper>
      <Routes>
        <Route path="/" element={<Navigate replace to="overview" />} />
        <Route path="*" element={<Navigate replace to="overview" />} />
        <Route path="/overview" element={<OverviewTab project={project} reloadProject={loadProject} />} />
      </Routes>
    </Container>
  );
};

export default ProjectDetails;
